import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Loader from './components/Loader';

const InfiniteList = ({
  children,
  hasMore,
  onLoad,
  initialLoad = false,
  threshold = 100,
}) => {
  const { t } = useTranslation();
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);
  const initialLoadDone = useRef(false);

  const handleIntersect = useCallback(
    (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingRef.current) {
        loadingRef.current = true;
        onLoad();
        // Reset loading flag after a short delay to allow state updates
        setTimeout(() => {
          loadingRef.current = false;
        }, 100);
      }
    },
    [hasMore, onLoad]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleIntersect, {
      root: null, // viewport
      rootMargin: `${threshold}px`,
      threshold: 0,
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersect, threshold]);

  // Handle initial load if enabled
  useEffect(() => {
    if (initialLoad && !initialLoadDone.current && hasMore) {
      initialLoadDone.current = true;
      onLoad();
    }
  }, [initialLoad, hasMore, onLoad]);

  return (
    <>
      {children}
      <div ref={sentinelRef} style={{ height: 1 }} />
      {hasMore && <Loader>{t('loading')}</Loader>}
    </>
  );
};

InfiniteList.propTypes = {
  children: PropTypes.node,
  hasMore: PropTypes.bool,
  onLoad: PropTypes.func.isRequired,
  initialLoad: PropTypes.bool,
  threshold: PropTypes.number,
};

export default InfiniteList;
